import { useQueryClient } from "react-query";
import {
  Accordion,
  AccordionButton, AccordionIcon,
  AccordionItem, AccordionPanel,
  Box, Button, Flex, Text,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { Fragment, useEffect, useState } from "react";
import {
  useDefaultSupplierQuestionnaireConfig,
  useFinalizeSupplierQuestionnaire,
  useGetScopeOneConsumptionTypes
} from "../../../../../../hooks/query";
import { showErrorToast, showSuccessToast } from "../../../../../../utils";
import { t } from "i18next";
import { hideAppSpinner, showAppSpinner } from "../../../../../../context/Store/slices/appSpinnerSlice";
import { ConfirmationDialog } from "../../../../../../components/ConfirmationDialog";
import { DeleteDialog } from "../../../../../DataEntryPlanning/components/DeleteDialog";
import * as React from "react";
import { ConsumptionsGrid, ConsumptionsModal } from "../ConsumptionsGrid";
import {
  consumptionBasedScopeOneKeys,
  useAddConsumptionBasedDirectEmissionsItem, useDeleteConsumptionBasedDirectEmissionsItem,
  useGetConsumptionBasedDirectEmissionsItems, useUpdateConsumptionBasedDirectEmissionsItem
} from "../../../../../../hooks/query/supplier-questionnaire/tabs";
import { defaultScopeOneGridColumns, mapScopeOneFormDataToApiData, SCOPES } from "../../helpers";

export const directEmissionsConsumptionTypes = [
  "stationaryConsumptionTypes",
  "mobileConsumptionTypes",
  "refrigerantConsumptionTypes"
];

function ConsumptionBasedScopeOne({
                                    isTabActive,
                                    defaultValues,
                                    handleTabChange,
                                    isPreview,
                                    returnToListRequested,
                                    returnToList
                                  }) {
  const { id: questionnaireId } = defaultValues;

  const queryClient = useQueryClient();
  const toast = useToast();
  const dispatch = useDispatch();

  const [changeTabDirection, setChangeTabDirection] = useState("");
  const [sectionsSize, setSectionsSize] = useState({});

  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const {
    isOpen: isConsumptionsModalOpen,
    onOpen: onConsumptionsModalOpen,
    onClose: onConsumptionsModalClose
  } = useDisclosure();
  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const buttonsWidth = useBreakpointValue({
    base: "24",
    md: "32"
  });

  const STATIONARY_SECTION = "stationary";
  const MOBILE_SECTION = "mobile";
  const REFRIGERANT_SECTION = "refrigerant";

  const [selectedItem, setSelectedItem] = useState(null);
  const [consumptionsModalData, setConsumptionsModalData] = useState(null);

  const {
    data: consumptionTypes
  } = useGetScopeOneConsumptionTypes({
    onError: err => err.response?.status !== 404 ? showErrorToast(toast, err.message) : null,
    onSettled: () => dispatch(hideAppSpinner())
  });

  useEffect(() => {
    if (changeTabDirection !== "") {
      if (changeTabDirection === "previous") {
        handleTabChange(prev => prev - 1);
        setChangeTabDirection("");
      } else if (changeTabDirection === "next") {
        handleTabChange(prev => prev + 1);
        setChangeTabDirection("");
      }
    }
  }, [changeTabDirection, handleTabChange]);

  useEffect(() => {
    if (isTabActive === true && returnToListRequested === true) {
      returnToList();
    }
  }, [isTabActive, returnToListRequested, returnToList]);

  const { mutate: finalize } = useFinalizeSupplierQuestionnaire({
    ...useDefaultSupplierQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, t("supplierQuestionnaire.page.finalized"));
      returnToList();
    }
  });

  const { mutate: addItem } = useAddConsumptionBasedDirectEmissionsItem({
    onSuccess: () => {
      handleConsumptionsModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(consumptionBasedScopeOneKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: updateItem } = useUpdateConsumptionBasedDirectEmissionsItem({
    onSuccess: () => {
      handleConsumptionsModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(consumptionBasedScopeOneKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const { mutate: deleteItem } = useDeleteConsumptionBasedDirectEmissionsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(consumptionBasedScopeOneKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleFinalizeConfirmation = () => {
    finalize(questionnaireId);
    onConfirmDialogClose();
  };

  const handleDeleteItemClick = (type, item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id, type: type });
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id, type: selectedItem.type });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const handleEditItemClick = (type, item) => {
    setConsumptionsModalData({
      consumptionTypes,
      scope: SCOPES.SCOPE_ONE,
      fieldArrayName: type,
      consumptionTypesAttr: directEmissionsConsumptionTypes.find(el => el.includes(type)),
      editData: item,
      columnNames: defaultScopeOneGridColumns
    });
    onConsumptionsModalOpen();
  };

  const handleAddItemClick = (type, sectionType) => {
    setConsumptionsModalData({
      consumptionTypes,
      scope: SCOPES.SCOPE_ONE,
      fieldArrayName: sectionType,
      consumptionTypesAttr: type,
      columnNames: [...defaultScopeOneGridColumns, ""]
    });
    onConsumptionsModalOpen();
  };

  const handleConsumptionsModalSubmit = (data) => {
    const apiData = mapScopeOneFormDataToApiData(data[consumptionsModalData.fieldArrayName]);
    dispatch(showAppSpinner());
    if (consumptionsModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0],
        type: consumptionsModalData.fieldArrayName
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData,
        type: consumptionsModalData.fieldArrayName
      });
    }
  };

  const handleConsumptionsModalClose = () => {
    setConsumptionsModalData(null);
    onConsumptionsModalClose();
  };

  const handleSectionSizeChange = (section, size) => {
    setSectionsSize({
      ...sectionsSize,
      [section]: size
    });
  };

  return (
    <Fragment>
      {isPreview && sectionsSize && Object.keys(sectionsSize).length === directEmissionsConsumptionTypes.length &&
      typeof Object.keys(sectionsSize).find(key => sectionsSize[key] > 0) === "undefined" ?
        <Text fontSize="lg" fontWeight="bold">
          {t("supplierQuestionnaire.consumptionBasedRevenue.noDataAvailable")}
        </Text> :
        <Box overflow="auto">
          <Accordion defaultIndex={[0]} allowMultiple>
            {directEmissionsConsumptionTypes.map(type => {
              if (isPreview && sectionsSize && Object.keys(sectionsSize).length > 0 && sectionsSize[type] === 0) {
                return null;
              }
              const sectionTitleKey = type.split("ConsumptionTypes")[0];
              let sectionType = "";
              switch (sectionTitleKey) {
                case "stationary":
                  sectionType = STATIONARY_SECTION;
                  break;
                case "mobile":
                  sectionType = MOBILE_SECTION;
                  break;
                case "refrigerant":
                  sectionType = REFRIGERANT_SECTION;
                  break;
                default:
                  sectionType = "";
              }

              return <AccordionItem
                key={`accordion-item-${type}`}
                sx={{
                  ".chakra-collapse": {
                    padding: "20px 0"
                  }
                }}
              >
                {({ isExpanded }) => {
                  return <>
                    <h2>
                      <AccordionButton>
                        <Box flex="1" textAlign="left">
                          <Text as="h2" fontSize="heading.m" fontWeight="700" color="gray.600">
                            {t(`organizationQuestionnaire.scope1.inputs.${sectionTitleKey}Title`)}
                          </Text>
                        </Box>
                        <Text fontSize={"sm"} mr={2}>
                          {isExpanded ? t("buttons.collapse") : t("buttons.expand")}
                        </Text>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel overflow="auto">
                      {questionnaireId && consumptionTypes && (
                        <ConsumptionsGrid
                          questionnaireId={questionnaireId}
                          consumptionTypes={consumptionTypes}
                          scope={SCOPES.SCOPE_ONE}
                          sectionType={sectionType}
                          consumptionTypesAttr={type}
                          getItemsFunction={useGetConsumptionBasedDirectEmissionsItems}
                          handleAddItem={() => handleAddItemClick(type, sectionType)}
                          handleEditItem={(dataItem) => handleEditItemClick(sectionType, dataItem)}
                          handleDeleteItem={(dataItem) => handleDeleteItemClick(sectionType, dataItem)}
                          isPreview={isPreview}
                          onDataItemsChange={handleSectionSizeChange}
                        />
                      )}
                    </AccordionPanel>
                  </>;
                }}
              </AccordionItem>;
            })}
          </Accordion>
        </Box>
      }
      {!isPreview &&
        <Flex
          direction="row"
          justify="space-between"
          gap={4}
          mt={8}
          position="sticky"
          bottom="0"
          bg="white"
          borderTop="1px solid"
          borderColor="gray.200"
          py={4}
          zIndex="10"
        >
          <Flex gap="2">
            <Button
              bg="limeShade.100"
              color="pureBlack"
              _hover={{ bg: "limeShade.200" }}
              width="32"
              type="submit"
              onClick={() => {
                setChangeTabDirection("previous");
              }}
            >
              {t("buttons.previous")}
            </Button>
            <Button
              bg="limeShade.100"
              color="pureBlack"
              _hover={{ bg: "limeShade.200" }}
              width="32"
              type="submit"
              onClick={() => {
                setChangeTabDirection("next");
              }}
            >
              {t("buttons.next")}
            </Button>
          </Flex>
          <Button
            bg="lime"
            color="pureBlack"
            _hover={{ bg: "limeShade.300" }}
            width={buttonsWidth}
            onClick={() => {
              onConfirmDialogOpen();
            }}
          >
            {t("buttons.finalize")}
          </Button>
        </Flex>
      }
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isConsumptionsModalOpen && consumptionsModalData !== null && (
          <ConsumptionsModal
            modalData={consumptionsModalData}
            isOpen={isConsumptionsModalOpen}
            onClose={handleConsumptionsModalClose}
            onSubmit={handleConsumptionsModalSubmit}
            scope={SCOPES.SCOPE_ONE}
          />
        )
      }
    </Fragment>
  );
}

export default ConsumptionBasedScopeOne;